import { useMemo, useState } from "react";

import { useBackend } from "../app/BackendProvider";
import { Autocomplete } from "../app/Autocomplete";
import { formatDuration, formatIsoTime, isAbsentVehicleNumber } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { describeError, useResource } from "../app/useResource";
import { useActivePostParam } from "../posts/PostProvider";
import { useLiveJourneys } from "./hooks";
import { JourneyPicker } from "./JourneyPicker";
import type { ToolsScope } from "./toolsScope";

const stopSearchTerms = (s: { id: string; name: string }) => [s.name, s.id];

/**
 * Outil « prochain passage » (P2-16, contrat v1.15). Deux usages depuis un même
 * champ :
 *  - un arrêt seul → les prochains véhicules qui s'y présentent, dans l'ordre ;
 *  - + un n° de parc → le prochain passage de ce véhicule à cet arrêt.
 * Consultation pure — les navettes / TAE / TAD y figurent (aucune exclusion de
 * périmètre décisionnel).
 *
 * Portée « véhicule » : le n° de parc est celui du véhicule sélectionné (verrouillé,
 * on ne choisit que l'arrêt) ; « réseau » : filtre véhicule optionnel, comme avant.
 */
export function NextPassagesPanel({ scope }: { scope: ToolsScope }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();
  const { items: journeys } = useLiveJourneys(postParam);

  const stopsQuery = useResource((signal) => backend.api.getStops(networkId, {}, signal), [backend, networkId]);
  const stops = useMemo(
    () => [...(stopsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [stopsQuery.data],
  );

  const [stop, setStop] = useState<(typeof stops)[number] | null>(null);
  const stopId = stop?.id ?? null;
  const [vehicle, setVehicle] = useState("");
  // Le n° de parc reste une saisie libre (l'API le filtre tel quel) ; s'il
  // correspond à une course en circulation, on l'affiche comme choisi.
  const withVehicle = useMemo(
    () => journeys.filter((j) => !isAbsentVehicleNumber(j.vehicle?.displayNumber)),
    [journeys],
  );
  const vehicleJourney = useMemo(
    () => withVehicle.find((j) => j.vehicle?.displayNumber === vehicle.trim()) ?? null,
    [withVehicle, vehicle],
  );

  const scopedVehicle =
    scope.kind === "vehicle" ? (isAbsentVehicleNumber(scope.journey.vehicle?.displayNumber) ? "" : scope.journey.vehicle!.displayNumber) : null;
  const veh = scopedVehicle ?? vehicle.trim();
  const passagesQuery = useResource(
    (signal) =>
      stopId
        ? backend.api.getNextPassages(networkId, { stopId, ...(veh ? { vehicle: veh } : {}), limit: 12 }, signal)
        : Promise.resolve(null),
    [backend, networkId, stopId, veh],
  );

  const data = passagesQuery.data;

  return (
    <div>
      <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
        <div className="is-size-7" style={{ display: "grid", gap: 3 }}>
          <span className="has-text-grey">Arrêt</span>
          <Autocomplete
            label="Arrêt"
            placeholder="Nom de l'arrêt"
            options={stops}
            getKey={(s) => s.id}
            getSearchTerms={stopSearchTerms}
            getSelectedText={(s) => s.name}
            selected={stop}
            onSelect={setStop}
            renderOption={(s) => (
              <span style={{ fontSize: "0.78rem" }}>
                {s.name} <span className="mono has-text-grey">· {s.id}</span>
              </span>
            )}
            emptyText="Aucun arrêt ne correspond."
          />
        </div>
        {scope.kind === "vehicle" ? (
          <p className="is-size-7" style={{ margin: 0 }}>
            <span className="has-text-grey">Véhicule · </span>
            {scopedVehicle ? (
              <strong>bus {scopedVehicle}</strong>
            ) : (
              <span className="has-text-grey">non identifié — passages de tous les véhicules à l'arrêt</span>
            )}
          </p>
        ) : (
          <div className="is-size-7" style={{ display: "grid", gap: 3 }}>
            <span className="has-text-grey">Véhicule (optionnel) — n° de parc, ligne ou sortie</span>
            <JourneyPicker
              label="Véhicule"
              placeholder="ex. 2317, T3, T3-04…"
              journeys={withVehicle}
              selected={vehicleJourney}
              onSelect={(j) => setVehicle(j?.vehicle?.displayNumber ?? "")}
            />
          </div>
        )}
      </div>

      {stopsQuery.error ? (
        <p className="has-text-danger is-size-7">{describeError(stopsQuery.error)}</p>
      ) : !stopId ? (
        <p className="has-text-grey is-size-7">Choisir un arrêt pour voir les prochains passages.</p>
      ) : passagesQuery.loading && !data ? (
        <p className="has-text-grey is-size-7">Recherche…</p>
      ) : passagesQuery.error ? (
        <p className="has-text-danger is-size-7">{describeError(passagesQuery.error)}</p>
      ) : !data || data.passages.length === 0 ? (
        <p className="has-text-grey is-size-7">
          Aucun passage prévu {veh ? `pour le véhicule ${veh} ` : ""}
          {data?.stopName ? `à « ${data.stopName} »` : "à cet arrêt"} dans l'horizon des courses en circulation.
        </p>
      ) : (
        <>
          <div className="ovl" style={{ marginBottom: 6 }}>
            {data.stopName ?? "Arrêt"} · {data.passages.length} passage{data.passages.length > 1 ? "s" : ""}
          </div>
          <table className="table is-fullwidth is-narrow" style={{ fontSize: "0.72rem" }}>
            <thead>
              <tr>
                <th>Dans</th>
                <th>Ligne</th>
                <th>Destination</th>
                <th>Véh.</th>
                <th style={{ textAlign: "right" }}>Écart</th>
              </tr>
            </thead>
            <tbody>
              {data.passages.map((p) => (
                <tr key={p.journeyId} style={p.skipped ? { opacity: 0.5, textDecoration: "line-through" } : undefined}>
                  <td className="mono" style={{ whiteSpace: "nowrap" }}>
                    {p.etaSeconds <= 30 ? "imminent" : `${formatDuration(p.etaSeconds)}`}
                    <br />
                    <span className="has-text-grey">{formatIsoTime(p.expected ?? p.scheduled)}</span>
                  </td>
                  <td>
                    <span className="mono" style={{ fontWeight: 700 }}>
                      {p.lineCode}
                    </span>
                  </td>
                  <td>{p.headsign}</td>
                  <td className="mono">{p.vehicleDisplayNumber ?? "—"}</td>
                  <td className="mono" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {p.deviationSeconds > 0 ? "+" : p.deviationSeconds < 0 ? "−" : ""}
                    {formatDuration(Math.abs(p.deviationSeconds))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
